/**
 * The ledger transactions TABLE's own chrome: column titles, the per-row
 * action, the reconciliation tag, the empty-state and toast copy. Read by the
 * table shell, its controller and the two pages that mount it; nothing in
 * `utils/` needs any of it.
 *
 * Everything the ledger itself speaks — types and their labels, advance types,
 * processed status, the zone, the timestamp formats — and the table STATE the
 * URL persists (sort keys, filter options, the column-key → `sortBy` map) live
 * in `src/constants/ledger.constants.js`, where `utils/ledger.js` and
 * `utils/dates.js` can read them without reaching into a component folder.
 * Readers import them from there; nothing is re-exported here, so every
 * constant has one import path.
 */
import { ADVANCE_TYPE_LABELS, LEDGER_RECONCILIATION_FILTER } from '../../../constants/ledger.constants';

/**
 * Column headings. One definition so the two screens cannot label a column
 * differently. Created and Processed take the zone marker as a suffix at render
 * time (`Created (ET)`), because the zone comes from the payload.
 */
export const LEDGER_COLUMN_TITLES = Object.freeze({
    USER: 'User',
    TYPE: 'Type',
    ADVANCE_TYPE: 'Advance Type',
    AMOUNT: 'Amount',
    FEE: 'Fee',
    CREATED: 'Created',
    PROCESSED: 'Processed',
    DESTINATION: 'Destination',
});

/**
 * Accessible name for a cell that is deliberately empty — advance type on a
 * settlement or a fee. The cell renders visually blank (no stray dash) and
 * carries this for screen readers.
 */
export const LEDGER_EMPTY_CELL_LABEL = 'Not applicable';

/**
 * Reconciliation marker, rendered beside the Type tag on a settlement written by
 * the admin reconcile action (`is_reconciliation` from the endpoint). Text, not
 * only colour: the row is also kept off the green tone, but the word is what
 * makes it distinguishable from a real repayment with colour ignored.
 */
export const LEDGER_RECONCILIATION_TAG_LABEL = 'Reconciliation';
export const LEDGER_RECONCILIATION_TAG_TITLE =
    'Booked by an admin to zero out the balance — not money the member repaid';

/**
 * Labels for the two reconciliation filter values that narrow the set. INCLUDE
 * has none here on purpose: it is the "no filter" state, and the control that
 * offers it names it in its own terms (`RECONCILIATION_FILTER_CHIP_OPTIONS` in
 * reports.constants says "Reconciliations: All") — one spelling, not two.
 */
export const LEDGER_RECONCILIATION_FILTER_LABELS = Object.freeze({
    [LEDGER_RECONCILIATION_FILTER.EXCLUDE]: 'Exclude reconciliations',
    [LEDGER_RECONCILIATION_FILTER.ONLY]: 'Only reconciliations',
});

/** Advance-type options for a filter control, from the one Instant/Standard vocabulary. */
export const ADVANCE_TYPE_FILTER_OPTIONS = Object.freeze(
    Object.entries(ADVANCE_TYPE_LABELS).map(([value, label]) => ({ value, label })),
);

/**
 * What the per-row action button does. The two consumers differ here and
 * nowhere else in the columns: the User page opens a support ticket for the
 * row, the Reports tab jumps to the row's user.
 */
export const LEDGER_ROW_ACTION = Object.freeze({
    OPEN_TICKET: 'openTicket',
    VIEW_USER: 'viewUser',
});

export const LEDGER_ROW_ACTION_LABELS = Object.freeze({
    [LEDGER_ROW_ACTION.OPEN_TICKET]: { title: 'Open a ticket', ariaLabel: 'Open ticket for transaction' },
    [LEDGER_ROW_ACTION.VIEW_USER]: { title: 'View user', ariaLabel: 'View user for transaction' },
});

/**
 * Empty-state copy. Three different facts, three different sentences: the
 * fetch failed (the rows were cleared, so "no transactions" would be false), a
 * filter matched nothing (the member may well have transactions — just not of
 * this kind), and a genuinely empty ledger. An empty table with no message
 * would read as "this member has no transactions" in every case.
 */
export const LEDGER_EMPTY_DESCRIPTION = Object.freeze({
    NO_TRANSACTIONS: 'No transactions yet.',
    NO_MATCH: 'No transactions match these filters.',
    FAILED: 'Could not load transactions.',
});

/**
 * The control beside the failure sentence. A control rather than an
 * instruction: telling someone to reload the page asks them to throw away the
 * rest of the page to retry one request, and the retry re-reads the query
 * string, so it lands on exactly the page, sort and filter the headers show.
 */
export const LEDGER_RETRY_LABEL = 'Try again';

/** The way out of the no-match state. */
export const LEDGER_CLEAR_FILTERS_LABEL = 'Clear filters';

/**
 * Toast copy when a page of transactions fails to load. Beside the failed
 * empty-state copy so the two screens that show this table say the same thing.
 */
export const LEDGER_LOAD_TOAST = Object.freeze({
    FAILED: 'Failed to load transactions. Please try again.',
});
